import type { NotificationData } from '@jobilla/nexus-sdk'

type CandidateTab = 'responses' | 'emails' | 'whatsapp' | 'interviews'

type NotificationRouteDescriptor =
	| {
		to: '/company/$companyId/$campaignId/candidates/$candidateId'
		params: {
			companyId: string
			campaignId: string
			candidateId: string
		}
		search: { tab: CandidateTab }
	}
	| {
		to: '/company/$companyId/settings/whatsapp'
		params: { companyId: string }
	}
	| {
		to: '/account/calendar'
	}
	| {
		to: '/company/$companyId/$campaignId/overview'
		params: { companyId: string; campaignId: string }
	}

const TAB_BY_TYPE: Record<string, CandidateTab> = {
	candidate_reply: 'emails',
	candidate_note_mention: 'responses',
	whatsapp_reply: 'whatsapp',
	whatsapp_ai_conversation_ended: 'whatsapp',
	interview_booked: 'interviews',
	interview_cancelled: 'interviews',
	interview_stage_move_failed: 'interviews',
}

// The companyId is sourced from the notification payload (not the current route)
// so a user with access to multiple companies can't navigate into the wrong tenant
// when opening a notification belonging to a different company.
export function notificationRoute(notification: NotificationData): NotificationRouteDescriptor | null {
	const { type: discriminator, candidateId, campaignId, companySlug: companyId } = notification.data

	// Template approval/rejection is org-scoped, not candidate-scoped — it opens WhatsApp settings.
	if (discriminator === 'whatsapp_template_status' || discriminator === 'whatsapp_inactivity') {
		return companyId ? { to: '/company/$companyId/settings/whatsapp', params: { companyId } } : null
	}

	// A broken calendar is personal (user-global), not tied to a company or candidate —
	// it opens the calendar settings page where the reconnect action lives.
	if (discriminator === 'calendar_connection_broken') {
		return { to: '/account/calendar' }
	}

	// A campaign closed with its ATS job is about the campaign, not a candidate.
	if (discriminator === 'campaign_closed_with_ats_job') {
		return campaignId && companyId
			? { to: '/company/$companyId/$campaignId/overview', params: { companyId, campaignId } }
			: null
	}

	if (!candidateId || !campaignId || !companyId) {
		return null
	}

	const tab = TAB_BY_TYPE[discriminator]
	if (!tab) {
		return null
	}

	return {
		to: '/company/$companyId/$campaignId/candidates/$candidateId',
		params: { companyId, campaignId, candidateId },
		search: { tab },
	}
}
